import React, {Component} from "react";
import CustomerOfferEntity, {CustomerOfferRuleIds} from "../../data/entities/CustomerOfferEntity";
import { FormattedMessage } from "react-intl";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import moment from "moment-timezone";
import RewardsList from "./RewardsList";
import {GameEntity} from "../../data/entities/GameEntity";
import {UserDataEntity} from "../../data/entities/UserDataEntity";
import {AdobeAnalyticsContext} from "../../context/AdobeAnalyticsContext";
import CustomerOfferPeriodSwitcherArrows from "./CustomerOfferPeriodSwitcherArrows";
import CustomerOfferPeriodSwitcherTabs from "./CustomerOfferPeriodSwitcherTabs";

interface Props {
    locale: string;
    index: number;
    offer: CustomerOfferEntity;
    rewardsLeft: number;
    games: GameEntity[];
    claimReward: (offerIndex: number, rewardIndex: number, callback: (success: boolean) => void) => void;
    refreshOffers: () => void;
    user: UserDataEntity;
}

interface State {
    period: number;
    remainingTime: string;
}

export default class CustomerOffer extends Component<Props, State> {
    static contextType = AdobeAnalyticsContext;
    context!: React.ContextType<typeof AdobeAnalyticsContext>;

    private  remainingTimeInterval: ReturnType<typeof setTimeout> | null = null;

    state: State = {
        period: 0,
        remainingTime: moment(this.props.offer.endDate).fromNow(true),
    };

    componentDidMount(): void {
        this.remainingTimeInterval = setInterval(this.calculateRemainingTime, 10000);
    }

    componentWillUnmount(): void {
        if (this.remainingTimeInterval) {
            clearInterval(this.remainingTimeInterval);
        }
    }

    render(): JSX.Element|null {
        if (this.isTrialOffer()) {
            return null;
        }

        return (
            <div className="offer">
                {this.title()}
                {this.subtitle()}

                <CustomerOfferPeriodSwitcherArrows
                    period={this.state.period}
                    switchToPreviousPeriod={this.switchToPreviousPeriod}
                    switchToNextPeriod={this.switchToNextPeriod}
                />

                <CustomerOfferPeriodSwitcherTabs
                    period={this.state.period}
                    switchToHistory={this.switchToHistory}
                    switchToCurrentOffer={this.switchToCurrentOffer}
                    switchToNextOffer={this.switchToNextOffer}
                />

                <RewardsList
                    rewards={this.props.offer.rewards}
                    games={this.props.games}
                    ruleId={this.props.offer.rule ? this.props.offer.rule.id : 0}
                    locale={this.props.locale}
                    period={this.state.period}
                    rewardsLeft={this.props.rewardsLeft}
                    claimReward={this.claimReward}
                />
            </div>
        );
    }

    private title = (): JSX.Element | null => {
        if (this.props.offer.rule === null) {
            return null;
        } else {
            return <h3 className="offer-title"><FormattedMessage id="rewards.monthly_rewards"/></h3>;
        }
    };

    private subtitle = (): JSX.Element | null => {
        return (
            <p className="offer-subtitle">
                <FontAwesomeIcon icon={["far", "clock"]}/>
                &nbsp;&nbsp;
                {
                    this.isTrialOffer() ?
                        <FormattedMessage id="rewards.trial_info"/>
                        :
                        <FormattedMessage
                            id="rewards.left"
                            values={{
                                time_left: <strong>{this.state.remainingTime}</strong>,
                                rewards_left: (
                                    <strong className={this.props.rewardsLeft > 0 ? "text-primary" : "text-danger"}>
                                        {this.props.rewardsLeft >= 0 ? this.props.rewardsLeft : 0}
                                    </strong>
                                )
                            }}
                        />
                }
            </p>
        );
    };

    private isTrialOffer = (): boolean => {
        if (this.props.offer.rule === null) {
            return false;
        }

        return (
            this.props.offer.rule.id === CustomerOfferRuleIds.progressionBased &&
            parseInt(this.props.offer.rule.value) === 0
        );
    };

    private calculateRemainingTime = (): void => {
        let endDate = moment(this.props.offer.endDate);

        if (moment(endDate).diff(moment()) < 0) {
            this.props.refreshOffers();
            return;
        }

        this.setState({
            remainingTime: endDate.fromNow(true)
        });
    };

    switchToPreviousPeriod = (): void => {
        this.context.track("clickevent", {location: "rewards", category: "interaction", action: "previous offer"});
        this.setState(prevState => ({
            period: prevState.period - 1
        }));
    };

    switchToNextPeriod = (): void => {
        this.context.track("clickevent", {location: "rewards", category: "interaction", action: "next offer"});
        this.setState(prevState => ({
            period: prevState.period + 1
        }));
    };

    switchToHistory = (): void => {
        this.context.track("clickevent", {location: "rewards", category: "interaction", action: "history"});
        this.setState({
            period: -1
        });
    };

    switchToCurrentOffer = (): void => {
        this.context.track("clickevent", {location: "rewards", category: "interaction", action: "current offer"});
        this.setState({
            period: 0
        });
    };

    switchToNextOffer = (): void => {
        this.context.track("clickevent", {location: "rewards", category: "interaction", action: "next offer"});
        this.setState({
            period: 1
        });
    };

    claimReward = (index: number, callback: (success: boolean) => void) => {
        this.props.claimReward(this.props.index, index, callback);
    };
}